import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import cx from 'clsx'

import { retainContainedLoader, releaseContainedLoader } from './containedRegistry'

import styles from './index.module.css'

export interface LoadingProps {
  loading: boolean
  redirectTo?: string
  redirectURL?: string
  // Fill the nearest positioned ancestor instead of the viewport. Use inside
  // modals/panels: the default fixed overlay tints the entire app while it
  // is mounted.
  contained?: boolean
  // Whether the overlay swallows pointer events. The default overlay sits at
  // `z-overlay` across the whole viewport with nothing to stop it capturing
  // clicks, which is right for an in-flight mutation (no double-submit) and
  // wrong for a background refetch. Defaults to the historical blocking
  // behaviour so existing call sites are unchanged.
  blocking?: boolean
}

export const Loading: React.FC<LoadingProps> = ({
  loading = false,
  redirectTo,
  redirectURL,
  contained = false,
  blocking = true,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'auth' })

  // Let the app-level loaders know a page is showing its own loader, so they
  // do not stack a second viewport-centred spinner on the same fetch.
  const isContainedAndVisible = contained && loading && !redirectTo
  useEffect(() => {
    if (!isContainedAndVisible) return
    retainContainedLoader()
    return releaseContainedLoader
  }, [isContainedAndVisible])

  return redirectTo ? (
    <>
      <div className={styles.redirectBg}>
        <div className="absolute box-content px-4 max-w-xs max-h-sm transform flex flex-col justify-center items-center gap-9">
          <img src={`/${redirectTo.toLowerCase()}-redirect.svg`} alt={`Redirecting to ${redirectTo}...`} />
          <a
            href={redirectURL}
            className="w-fit bg-white px-4 py-2 rounded-xl font-bold text-brand_primary whitespace-break-spaces	flex items-center"
          >
            {t('redirectingTo')} {redirectTo}
            {'  '}
            <span className="spinner-border" />
          </a>
        </div>
      </div>
    </>
  ) : (
    loading && (
      <div
        className={cx(
          'progress bg-[rgba(236,_240,_241,_0.5)]',
          contained ? 'absolute inset-0' : 'fixed top-0 left-0 w-full h-full z-overlay',
          !blocking && 'pointer-events-none',
        )}
      >
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[50px] h-[50px] ">
          <div className="w-full h-full rounded-[50%] bg-brand_primary opacity-60 absolute top-0 left-0 animate-[sk-bounce_2s_infinite_ease-in-out]" />
          <div className="w-full h-full rounded-[50%] bg-brand_secondary opacity-60 absolute top-0 left-0 animate-[sk-bounce_1s_infinite_ease-in-out] content-[' ']" />
        </div>
      </div>
    )
  )
}

export default Loading
